import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Lock, ShieldCheck, Trash2, UserRound } from 'lucide-react';
import type { Paginated, PlatformUserRow } from '@worki/shared';
import { api, buildQuery } from '../../lib/api.js';
import { useDebounced } from '../../lib/useDebounced.js';
import { currentLocale } from '../../i18n.js';
import { formatDate } from '../../lib/format.js';
import { Button, EmptyState, Select, Spinner } from '../../components/ui.js';
import { PageSearch } from '../../components/PageSearch.js';
import { ConfirmDialog, Modal } from '../../components/Modal.js';
import { AdminCard, Pill, Row, useAdminAction } from './parts.js';
import { removalCopy } from './userRemoval.js';

/**
 * ADM-002 — everyone who can sign in, across every business.
 *
 * A Worki account is one person, so this screen has exactly two questions to
 * answer about a row, and neither is what rank they hold inside their business:
 * may they sign in, and are they Worki's own staff. It used to ask a third —
 * admin or member — from the days when a plan sold seats and a business
 * administered its own. A filter with one possible answer sorted every account
 * into the same bucket and left the other one permanently empty.
 *
 * It is a search rather than a per-business list because support arrives
 * holding an email address and not an account id.
 *
 * Two ways out, deliberately not one: suspending keeps the person's name on
 * everything they wrote and can be undone; deleting takes the attribution with
 * it and cannot.
 */
const PAGE_SIZE = 30;

export function UsersSection() {
  const { t } = useTranslation();
  const locale = currentLocale();

  const [search, setSearch] = useState('');
  const [staff, setStaff] = useState('');
  const [state, setState] = useState('');
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<PlatformUserRow | null>(null);
  const [removing, setRemoving] = useState<PlatformUserRow | null>(null);
  const debounced = useDebounced(search, 300);

  // A filter change that keeps the old page number lands on an empty page.
  useEffect(() => setPage(1), [debounced, staff, state]);

  const list = useQuery({
    queryKey: ['platform', 'users', { debounced, staff, state, page }],
    queryFn: () =>
      api.get<Paginated<PlatformUserRow>>(
        `/platform/users${buildQuery({
          q: debounced,
          staff,
          active: state === '' ? undefined : state === 'active',
          page,
          pageSize: PAGE_SIZE,
        })}`,
      ),
  });

  const action = useAdminAction([
    ['platform', 'users'],
    ['platform', 'staff'],
    ['platform', 'businesses'],
    ['platform', 'business'],
  ]);

  const rows = list.data?.items ?? [];
  const total = list.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  // Deleting the last account in a business deletes the business, which is a
  // different sentence and a different confirmation line.
  const removal = removalCopy(removing);

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-2">
        <PageSearch value={search} onChange={setSearch} placeholder={t('admin.users.searchPlaceholder')} />
        <div className="grid grid-cols-2 gap-2 sm:w-72 shrink-0">
          <Select value={staff} aria-label={t('admin.users.allTypes')} onChange={setStaff}>
            <option value="">{t('admin.users.allTypes')}</option>
            <option value="true">{t('admin.users.onlyStaff')}</option>
            <option value="false">{t('admin.users.onlyCustomers')}</option>
          </Select>
          <Select value={state} aria-label={t('admin.users.allStates')} onChange={setState}>
            <option value="">{t('admin.users.allStates')}</option>
            <option value="active">{t('admin.users.onlyActive')}</option>
            <option value="inactive">{t('admin.users.onlyInactive')}</option>
          </Select>
        </div>
      </div>

      {list.isLoading ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : rows.length === 0 ? (
        <EmptyState icon={<UserRound className="h-7 w-7" />} title={t('admin.users.empty')} />
      ) : (
        <div className="space-y-2">
          {rows.map((user) => (
            <Row
              key={user.id}
              icon={<UserRound className="h-4 w-4" aria-hidden />}
              title={
                <span className="flex items-center gap-2">
                  <span className="truncate">{user.name}</span>
                  {user.isPlatformAdmin && <Pill tone="info">{t('admin.users.staff')}</Pill>}
                  {!user.active && <Pill tone="bad">{t('admin.users.onlyInactive')}</Pill>}
                  {user.access !== 'active' && (
                    <Pill tone="warn">
                      {t(`admin.approvals.${user.access === 'pending' ? 'pending' : 'suspended'}`)}
                    </Pill>
                  )}
                </span>
              }
              subtitle={
                <span dir="ltr" className="inline-block max-w-full truncate">
                  {user.email} · {user.businessName}
                </span>
              }
              meta={user.lastSeenAt ? formatDate(user.lastSeenAt, locale) : t('admin.users.never')}
              onClick={() => setEditing(user)}
            />
          ))}
        </div>
      )}

      {pages > 1 && (
        <div className="flex items-center justify-center gap-3 text-xs text-muted-foreground">
          <Button variant="ghost" disabled={page <= 1} onClick={() => setPage((current) => current - 1)}>
            {t('common.previous')}
          </Button>
          <span className="ltr-nums">
            {page} / {pages}
          </span>
          <Button variant="ghost" disabled={page >= pages} onClick={() => setPage((current) => current + 1)}>
            {t('common.next')}
          </Button>
        </div>
      )}

      <EditUserDialog
        user={editing}
        busy={action.busy}
        onClose={() => setEditing(null)}
        onSave={(patch) => {
          const target = editing;
          if (!target) return;
          void action.run(() => api.patch(`/platform/users/${target.id}`, patch), 'admin.users.saved').then((ok) => {
            if (ok) setEditing(null);
          });
        }}
        onRemove={() => {
          setRemoving(editing);
          setEditing(null);
        }}
      />

      <ConfirmDialog
        open={removing !== null}
        title={t(removal.title)}
        message={t(removal.message, removal.vars)}
        loading={action.busy}
        onCancel={() => setRemoving(null)}
        onConfirm={() => {
          const target = removing;
          if (!target) return;
          void action.run(() => api.del(`/platform/users/${target.id}`), removal.done).then((ok) => {
            if (ok) setRemoving(null);
          });
        }}
      />
    </div>
  );
}

function EditUserDialog({
  user,
  busy,
  onClose,
  onSave,
  onRemove,
}: {
  user: PlatformUserRow | null;
  busy: boolean;
  onClose: () => void;
  onSave: (patch: { active: boolean; isPlatformAdmin: boolean }) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const [active, setActive] = useState(true);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);

  /* Reopening the dialog on a different person must not carry the last one's
     half-made change into it. */
  useEffect(() => {
    if (!user) return;
    setActive(user.active);
    setIsPlatformAdmin(user.isPlatformAdmin);
  }, [user]);

  return (
    <Modal
      open={user !== null}
      title={t('admin.users.edit')}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={() => onSave({ active, isPlatformAdmin })} loading={busy}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      {user && (
        <div className="space-y-3">
          <AdminCard>
            <p className="text-sm font-medium">{user.name}</p>
            <p className="text-xs text-muted-foreground" dir="ltr">
              {user.email}
            </p>
            <p className="text-xs text-muted-foreground">{user.businessName}</p>
          </AdminCard>

          <label className="flex items-center gap-2.5 text-sm">
            <input
              type="checkbox"
              checked={active}
              onChange={(event) => setActive(event.target.checked)}
              className="h-4 w-4 shrink-0 rounded accent-primary"
            />
            {t('admin.users.active')}
          </label>

          {/* The environment list is the root of this whole surface and no code
              path writes it, so the switch is shown locked rather than offered
              and then refused: a control that accepts a change the next read
              undoes is worse than one that says no. */}
          <label className="flex items-center gap-2.5 text-sm">
            <input
              type="checkbox"
              checked={isPlatformAdmin}
              disabled={user.isRootStaff}
              onChange={(event) => setIsPlatformAdmin(event.target.checked)}
              className="h-4 w-4 shrink-0 rounded accent-primary disabled:opacity-50"
            />
            <span className="flex items-center gap-1.5">
              <ShieldCheck className="h-3.5 w-3.5 text-primary" aria-hidden />
              {t('admin.users.staff')}
            </span>
          </label>
          <p className="text-xs text-muted-foreground leading-relaxed ps-6">
            {user.isRootStaff ? (
              <span className="flex items-start gap-1.5">
                <Lock className="h-3.5 w-3.5 shrink-0 mt-0.5" aria-hidden />
                {t('admin.users.rootStaffHint')}
              </span>
            ) : (
              t('admin.users.staffHint')
            )}
          </p>

          <button
            type="button"
            onClick={onRemove}
            className="h-9 px-3 rounded-lg bg-destructive/10 text-destructive text-xs font-medium flex items-center gap-1.5 hover:bg-destructive/15 transition-colors"
          >
            <Trash2 className="h-3.5 w-3.5" />
            {t(removalCopy(user).title)}
          </button>
        </div>
      )}
    </Modal>
  );
}
